class CrossSellsVariantGrouped extends HTMLElement {
    constructor() {
        super();
        this.variantsScript = this.querySelector('[data-variants-json]');
        this.addBtn = this.querySelector('.cross-sells__item-add');
        this.priceElem = this.querySelector('.cross-sells__variant-price');
        this.btnTextElem = this.querySelector('.cross-sells__btn-text');
        this.imageLink = this.querySelector('.cross-sells__item-link');
        this.imageElem = this.querySelector('.cross-sells__item-image img'); 
        this.titleLink = this.querySelector('.cross-sells__item-title a');
    }

    connectedCallback() {
        this.initSelections();
        
        this.addEventListener('change', this.handleChange.bind(this));
        this.addEventListener('click', this.handleClick.bind(this));

        // Watch for new content if needed, though web components handle their own lifecycle
        // This is kept just in case child options are injected post-load
        this.observer = new MutationObserver((mutations) => {
            let shouldInit = false;
            for(const m of mutations) {
                if(m.addedNodes.length) {
                    shouldInit = true;
                    break;
                }
            }
            if(shouldInit) this.initSelections();
        });
        this.observer.observe(this, { childList: true, subtree: true });
        
        // Handle clicks outside for custom select closes
        document.addEventListener('click', this.handleDocumentClick.bind(this));
    }

    disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        document.removeEventListener('click', this.handleDocumentClick.bind(this));
    }

    formatMoney(cents) {
        if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
            const format = (typeof theme !== 'undefined' && theme.moneyFormat) ? theme.moneyFormat : '${{amount}}';
            return Shopify.formatMoney(cents, format);
        }
        return '$' + (cents / 100).toFixed(2);
    }

    handleVariantSelection(selectedOptions) {
        if (!this.variantsScript) return;
        
        let variants = [];
        try {
            variants = JSON.parse(this.variantsScript.textContent);
        } catch (err) {
            return;
        }

        const matchingVariant = variants.find(v => {
            if (!v.options || v.options.length !== selectedOptions.length) return false;
            return v.options.every((val, index) => val === selectedOptions[index]);
        });

        if (matchingVariant) {
            if (this.addBtn) {
                this.addBtn.setAttribute('data-add-item-id', matchingVariant.id);
                const newUrl = matchingVariant.url || `${window.location.pathname}?variant=${matchingVariant.id}`;
                this.addBtn.setAttribute('data-product-url', newUrl);
                this.addBtn.disabled = !matchingVariant.available;
                this.addBtn.classList.remove('disabled');
                
                if (matchingVariant.available) {
                    const baseText = (window.theme && window.theme.strings && window.theme.strings.addToCart) || 'Add to Cart';
                    if (this.btnTextElem) this.btnTextElem.textContent = baseText;
                } else {
                    const soldOutText = (window.theme && window.theme.strings && window.theme.strings.soldOut) || 'Sold Out';
                    if (this.btnTextElem) this.btnTextElem.textContent = soldOutText;
                }
            }

            if (this.priceElem) {
                this.priceElem.innerHTML = this.formatMoney(matchingVariant.price);
            }

            if (matchingVariant.featured_image && this.imageElem) {
                const newSrc = matchingVariant.featured_image.src;
                if (newSrc) {
                    this.imageElem.src = newSrc;
                    this.imageElem.srcset = ''; 
                }
            }
            
            if (this.titleLink && matchingVariant.url) this.titleLink.href = matchingVariant.url;
            if (this.imageLink && matchingVariant.url) this.imageLink.href = matchingVariant.url;

        } else {
            if (this.addBtn) {
                this.addBtn.disabled = true;
                this.addBtn.classList.add('disabled');
                const unavailableText = (window.theme && window.theme.strings && window.theme.strings.unavailable) || 'Unavailable';
                if (this.btnTextElem) this.btnTextElem.textContent = unavailableText;
            }
        }
    }

    onOptionInteraction(e) {
        const optionBtn = e.target.closest('.product-swatches-options__item, .cross-sells__option-btn, .cross-sells__option-text-link');
        const selectElem = e.target.closest('.cross-sells__option-select');
        
        if (!optionBtn && !selectElem) return;

        // Only prevent default on click to stop navigation if it's a simulated button
        if (e.type === 'click' && optionBtn) {
            e.preventDefault();
            e.stopPropagation();
        }

        if (optionBtn) {
            const actualGroup = optionBtn.closest('.cross-sells__option-group');
            if (actualGroup) {
                const allBtns = actualGroup.querySelectorAll('.product-swatches-options__item, .cross-sells__option-btn, .cross-sells__option-text-link');
                allBtns.forEach(btn => {
                    btn.classList.remove('selected');
                    if (btn.classList.contains('cross-sells__option-text-link')) {
                        btn.style.fontWeight = 'normal';
                        btn.style.color = '#6C6C6C';
                    }
                });
                
                optionBtn.classList.add('selected');
                if (optionBtn.classList.contains('cross-sells__option-text-link')) {
                    optionBtn.style.fontWeight = 'bold';
                    optionBtn.style.color = 'var(--color-text)'; 
                }

                const labelSpan = actualGroup.querySelector('[data-option-value-label]');
                let val = '';
                if (optionBtn.classList.contains('product-swatches-options__item')) {
                     val = optionBtn.getAttribute('data-variant-name');
                } else {
                     val = optionBtn.getAttribute('data-value');
                }
                if (labelSpan && val) labelSpan.textContent = val;
            }
        } else if (selectElem) {
             const actualGroup = selectElem.closest('.cross-sells__option-group');
             if (actualGroup) {
                 const labelSpan = actualGroup.querySelector('[data-option-value-label]');
                 if (labelSpan) labelSpan.textContent = selectElem.value;
             }
        }

        const allOptionGroups = this.querySelectorAll('.cross-sells__option-group');
        const selectedOptions = Array.from(allOptionGroups).map(grp => {
            const select = grp.querySelector('.cross-sells__option-select');
            if (select) return select.value;

            const sel = grp.querySelector('.product-swatches-options__item.selected, .cross-sells__option-btn.selected, .cross-sells__option-text-link.selected');
            if (sel) {
                return sel.classList.contains('product-swatches-options__item') 
                    ? sel.getAttribute('data-variant-name') 
                    : sel.getAttribute('data-value');
            }
            const lbl = grp.querySelector('[data-option-value-label]');
            return lbl ? lbl.textContent.trim() : null;
        });

        if (selectedOptions.some(s => !s)) return;
        
        this.handleVariantSelection(selectedOptions);
    }

    handleChange(e) {
        const selectElem = e.target.closest('.cross-sells__option-select');
        if (selectElem) {
            this.onOptionInteraction(e);
        }
    }

    handleDocumentClick(e) {
        const isTrigger = e.target.closest('.cross-sells__custom-select-trigger');
        const isOption = e.target.closest('.cross-sells__custom-select-option');
        if (!isTrigger && !isOption) {
            document.querySelectorAll('.cross-sells__custom-select-options').forEach(el => {
                if (el.style.display !== 'none' && el.style.display !== '') {
                    el.style.display = 'none';
                    el.classList.add('hidden');
                    if (el._wrapper) {
                        el._wrapper.appendChild(el);
                        el.style.position = '';
                        el.style.top = '';
                        el.style.left = '';
                        el.style.width = '';
                        el.style.right = '';
                        el.style.zIndex = '';
                    }
                }
            });
        }
        
        if (isOption) {
            // Options are in document.body so click doesn't bubble to component
            this.handleClick(e);
        }
    }

    handleClick(e) {
        const customSelectTrigger = e.target.closest('.cross-sells__custom-select-trigger');
        const customSelectOption = e.target.closest('.cross-sells__custom-select-option');

        if (customSelectTrigger) {
            e.preventDefault();
            const wrapper = customSelectTrigger.closest('.cross-sells__custom-select-wrapper');
            let optionsList = wrapper.querySelector('.cross-sells__custom-select-options');
            
            if (!optionsList && wrapper._optionsList) optionsList = wrapper._optionsList;
            if (!optionsList) return;
            
            wrapper._optionsList = optionsList;
            optionsList._wrapper = wrapper;
            
            const isHidden = optionsList.style.display === 'none' || optionsList.classList.contains('hidden');
            
            document.querySelectorAll('.cross-sells__custom-select-options').forEach(el => {
                if (el !== optionsList && el.style.display !== 'none' && el.style.display !== '') {
                    el.style.display = 'none';
                    el.classList.add('hidden');
                    if (el._wrapper) el._wrapper.appendChild(el);
                }
            });

            if (isHidden) {
                document.body.appendChild(optionsList);
                optionsList.style.display = 'block';
                optionsList.classList.remove('hidden');
                
                optionsList.style.position = 'fixed';
                optionsList.style.zIndex = '2147483647';
                
                const updatePosition = () => {
                    if (optionsList.style.display === 'none' || optionsList.classList.contains('hidden')) return;
                    
                    const rect = customSelectTrigger.getBoundingClientRect();
                    
                    // Always open upwards
                    optionsList.style.top = 'auto';
                    optionsList.style.bottom = (window.innerHeight - rect.top) + 'px';
                    optionsList.style.borderTop = '1px solid var(--color-border)';
                    optionsList.style.borderBottom = 'none';
                    optionsList.style.borderRadius = '4px 4px 0 0';
                    
                    optionsList.style.left = rect.left + 'px';
                    optionsList.style.width = rect.width + 'px';
                    
                    requestAnimationFrame(updatePosition);
                };
                
                updatePosition();
            } else {
                optionsList.style.display = 'none';
                optionsList.classList.add('hidden');
                wrapper.appendChild(optionsList);
                optionsList.style.position = '';
                optionsList.style.top = '';
                optionsList.style.left = '';
                optionsList.style.width = '';
                optionsList.style.right = '';
                optionsList.style.zIndex = '';
            }
            return;
        }

        if (customSelectOption) {
            const optionsList = customSelectOption.closest('.cross-sells__custom-select-options');
            const wrapper = optionsList._wrapper || customSelectOption.closest('.cross-sells__custom-select-wrapper');
            if (!wrapper) return;
            
            const trigger = wrapper.querySelector('.cross-sells__custom-select-trigger');
            const hiddenSelect = wrapper.querySelector('.cross-sells__option-select');
            
            trigger.querySelector('.cross-sells__custom-select-text').textContent = customSelectOption.querySelector('.cross-sells__custom-select-option-text').textContent;
            trigger.querySelector('.cross-sells__swatch-preview').style.cssText = customSelectOption.querySelector('.cross-sells__swatch-circle').getAttribute('data-swatch-style');
            hiddenSelect.value = customSelectOption.getAttribute('data-value');
            hiddenSelect.dispatchEvent(new Event('change', { bubbles: true }));

            optionsList.style.display = 'none';
            optionsList.classList.add('hidden');
            wrapper.appendChild(optionsList);
            optionsList.style.position = '';
            optionsList.style.top = '';
            optionsList.style.left = '';
            optionsList.style.width = '';
            optionsList.style.right = '';
            optionsList.style.zIndex = '';
            
            optionsList.querySelectorAll('.cross-sells__custom-select-option').forEach(opt => opt.classList.remove('selected'));
            customSelectOption.classList.add('selected');
            
            return;
        }

        this.onOptionInteraction(e);
    }

    initSelections() {
        const groups = this.querySelectorAll('.cross-sells__option-group');
        groups.forEach(grp => {
            if (grp.querySelector('.selected')) return;
            
            const lbl = grp.querySelector('[data-option-value-label]');
            if (lbl) {
                const val = lbl.textContent.trim();
                let btn = grp.querySelector(`.product-swatches-options__item[data-variant-name="${val.replace(/"/g, '\\"')}"]`);
                if (!btn) btn = grp.querySelector(`.cross-sells__option-btn[data-value="${val.replace(/"/g, '\\"')}"]`);
                if (!btn) btn = grp.querySelector(`.cross-sells__option-text-link[data-value="${val.replace(/"/g, '\\"')}"]`);
                
                if (btn) {
                    btn.classList.add('selected');
                    if (btn.classList.contains('cross-sells__option-text-link')) {
                        btn.style.fontWeight = 'bold';
                        btn.style.color = 'var(--color-text)'; 
                    }
                }
            }
        });
    }
}

if (!customElements.get('cross-sells-variant-grouped')) {
    customElements.define('cross-sells-variant-grouped', CrossSellsVariantGrouped);
}